import {typeFromValidation} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {RuleCondition} from '../../rules/ruleConditionModel';
import {decorateWithClientId, WithClientId} from '../commons/clientIdModel';

/*
 * Runtime type.
 */

function isCompoundConditions(input: unknown): input is CompoundConditions {
  return Array.isArray(input);
}

// Compound rules include legacy rules that make the possibilities almost endless.
// TODO: Implement io-ts type for RuleCompoundConditions, and monitor how much it can affect users.
export const RCompoundConditions = typeFromValidation('compoundConditions', isCompoundConditions);

/*
 * Type.
 */

export enum CompoundConditionTypesEnum {
  AND = 'and',
  OR = 'or',
  NOT = 'not',
}

export type SimpleCompoundConditions<ConditionType = RuleCondition> = [
  CompoundConditionTypesEnum,
  ...ConditionType[],
];

export type CompoundConditions<ConditionType = RuleCondition> = [
  CompoundConditionTypesEnum,
  ...(SimpleCompoundConditions<ConditionType> | ConditionType)[],
];

/*
 * Helpers.
 */

/**
 * Given compound conditions, returns the corresponding array of 'OR' children conditions,
 * decorated with client ids.
 */
export function parseCompoundConditions<ConditionType = RuleCondition>(
  compoundConditions: CompoundConditions<ConditionType> | undefined,
): ReadonlyArray<ReadonlyArray<WithClientId<ConditionType>>> {
  if (!compoundConditions) {
    return [];
  }

  const [op, ...conditions] = compoundConditions;

  // Bailout if top level operator is not 'AND'.
  if (op !== CompoundConditionTypesEnum.AND) {
    return [];
  }

  return conditions.map((conditionOrCompoundCondition) => {
    // ['or', cond1, cond2, cond3...] -> Push [cond1, cond2, cond3]
    if (Array.isArray(conditionOrCompoundCondition)) {
      const [, ...orConditions] = conditionOrCompoundCondition;
      return orConditions.map(decorateWithClientId);
    }

    // Single condition on its own -> push [cond]
    return [decorateWithClientId(conditionOrCompoundCondition)];
  });
}

export function buildCompoundConditions<ConditionType = RuleCondition>(
  conditions: ReadonlyArray<ReadonlyArray<ConditionType>>,
): CompoundConditions<ConditionType> {
  return [
    CompoundConditionTypesEnum.AND,
    ...conditions.map(
      (orConditions): SimpleCompoundConditions<ConditionType> => [
        CompoundConditionTypesEnum.OR,
        ...orConditions,
      ],
    ),
  ];
}

export function isSimpleCompoundConditions<ConditionType = RuleCondition>(
  conditions: SimpleCompoundConditions<ConditionType> | CompoundConditions<ConditionType>,
): conditions is SimpleCompoundConditions<ConditionType> {
  return !conditions.some((condition) => Array.isArray(condition));
}
